import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAddonsV1AddOnsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/add-ons-service',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/add-ons-service',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/add-ons-service',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/add-ons-service',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/add-ons-service',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/add-ons-service',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/add-ons-service',
        destPath: '',
      },
      {
        srcPath: '/addons/v1/add-ons',
        destPath: '/v1/add-ons',
      },
      {
        srcPath: '/addons/v1/bulk/add-ons',
        destPath: '/v1/bulk/add-ons',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/addons/v1/add-ons',
        destPath: '/v1/add-ons',
      },
      {
        srcPath: '/addons/v1/bulk/add-ons',
        destPath: '/v1/bulk/add-ons',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_add-ons';

/** Creates an add-on. */
export function createAddOn(payload: object): RequestOptionsFactory<any> {
  function __createAddOn({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'addOn.createdDate' }, { path: 'addOn.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'POST' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.CreateAddOn',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/add-ons',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'addOn.createdDate' },
              { path: 'addOn.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createAddOn;
}

/** Retrieves an add-on. */
export function getAddOn(payload: object): RequestOptionsFactory<any> {
  function __getAddOn({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'GET' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.GetAddOn',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/add-ons/{addOnId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'addOn.createdDate' },
              { path: 'addOn.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAddOn;
}

/**
 * Updates an existing add-on.
 *
 * Each time an add-on is updated, its revision number increments by 1.
 * The current revision must be passed when updating to ensure you're working with the latest version and prevent unintended overwrites.
 */
export function updateAddOn(payload: object): RequestOptionsFactory<any> {
  function __updateAddOn({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'addOn.createdDate' }, { path: 'addOn.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'PATCH' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.UpdateAddOn',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/add-ons/{addOn.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'addOn.createdDate' },
              { path: 'addOn.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateAddOn;
}

/** Deletes an add-on and removes it from all add-on groups. */
export function deleteAddOn(payload: object): RequestOptionsFactory<any> {
  function __deleteAddOn({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'DELETE' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.DeleteAddOn',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/add-ons/{addOnId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAddOn;
}

/**
 * Retrieves up to 100 add-ons, given the specified filtering, sorting, and paging.
 *
 * By default, results are sorted by `id` in ascending order.
 */
export function queryAddOns(payload: object): RequestOptionsFactory<any> {
  function __queryAddOns({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'GET' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.QueryAddOns',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/add-ons/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'addOns.createdDate' },
              { path: 'addOns.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixAddonsV1AddOnsServiceUrl({
            protoPath: '/v1/add-ons/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryAddOns;
}

/** Creates multiple add-ons in a single request. */
export function bulkCreateAddOns(payload: object): RequestOptionsFactory<any> {
  function __bulkCreateAddOns({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'addOns.createdDate' }, { path: 'addOns.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'POST' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.BulkCreateAddOns',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/bulk/add-ons/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateAddOns;
}

/** Updates multiple add-ons in a single request. */
export function bulkUpdateAddOns(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateAddOns({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'addOns.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'addOns.addOn.createdDate' },
          { path: 'addOns.addOn.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'POST' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.BulkUpdateAddOns',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/bulk/add-ons/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateAddOns;
}

/** Deletes multiple add-ons in a single request. */
export function bulkDeleteAddOns(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteAddOns({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.addons.v1.add_on',
      method: 'POST' as any,
      methodFqn: 'wix.addons.v1.AddOnsService.BulkDeleteAddOns',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAddonsV1AddOnsServiceUrl({
        protoPath: '/v1/bulk/add-ons/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteAddOns;
}
